import React, { createContext, useEffect } from 'react'

import useAuth from 'hooks/useAuth'
import Spinner from 'components/Spinner'
import AuthInterstitial from 'components/Factset/AuthInterstitial'
import { useUnleashContext, useFlag } from '@unleash/proxy-client-react'
import { FEATURE_FLAG_STANDALONE_PROVISIONING } from 'constants/featureFlag'
import { NEW_AUTH_KEY } from 'constants/localStorage'

const SessionStateContext = createContext()
const SessionHandlerContext = createContext()

function SessionProvider({ children }) {
  const { state, handlers } = useAuth()
  const updateContext = useUnleashContext()
  const isStandaloneProvisioning = useFlag(FEATURE_FLAG_STANDALONE_PROVISIONING)

  useEffect(() => {
    updateContext({ userId: state.currentUser?.id })
  }, [updateContext, state.currentUser])

  // Persists standalone-provisioning (FactSet auth) state so loginWithRedirect can read it
  // before the FF client loads. Skip when isSignedOut/isSessionExpired: the user needs
  // NEW_AUTH_KEY intact to get connection:'factset' on Sign In.
  useEffect(() => {
    if (state.isSignedOut || state.isSessionExpired) return
    if (isStandaloneProvisioning) {
      localStorage.setItem(NEW_AUTH_KEY, 'true')
    } else {
      localStorage.removeItem(NEW_AUTH_KEY)
    }
  }, [isStandaloneProvisioning, state.isSignedOut, state.isSessionExpired])

  if (state.isSignedOut) {
    return <AuthInterstitial message="You successfully logged out" onSignIn={handlers.signIn} />
  }

  if (state.isSessionExpired) {
    return (
      <AuthInterstitial
        message="You've been logged out due to inactivity"
        onSignIn={handlers.signIn}
      />
    )
  }

  if (state.isLoading || state.currentUser === null) {
    return <Spinner size={75} fullPage />
  }

  return (
    <SessionStateContext.Provider value={state}>
      <SessionHandlerContext.Provider value={handlers}>
        {state.currentUser && children}
      </SessionHandlerContext.Provider>
    </SessionStateContext.Provider>
  )
}

function useSessionState() {
  const context = React.useContext(SessionStateContext)
  if (context === undefined) {
    throw new Error('useSessionState must be used within an SessionProvider')
  }
  return context
}

function useSessionHandlers() {
  const context = React.useContext(SessionHandlerContext)
  if (context === undefined) {
    throw new Error('useSessionHandlers must be used within an SessionProvider')
  }
  return context
}

export { useSessionState, useSessionHandlers, SessionProvider, SessionHandlerContext }
